import { useEffect, useState } from "react";
import api from "../api/api";

export default function Contractors() {
  const emptyForm = {
    name: "",
    city: "",
    zip_code: "",
    street: "ul. ",
    pickup_city: "",
    pickup_zip_code: "",
    pickup_street: "ul. ",
    email: "",
    contact_person: "",
    phone: "",
    remarks: "",
    is_active: true
  };

  const formFields = [
    "name",
    "city",
    "zip_code",
    "street",
    "pickup_city",
    "pickup_zip_code",
    "pickup_street",
    "email",
    "contact_person",
    "phone",
    "remarks",
    "is_active"
  ];

  const [contractors, setContractors] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [backendError, setBackendError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [showPickup, setShowPickup] = useState(false);

  const [filters, setFilters] = useState({
    name: "",
    street: "",
    zip: "",
    city: "",
    email: "",
    phone: "",
  });


  const btn = {
    primary: "bg-blue-600 text-white text-sm font-semibold px-4 py-1.5 rounded hover:bg-blue-700 transition shadow-sm",
    success: "bg-green-600 text-white text-sm font-semibold px-4 py-1.5 rounded hover:bg-green-700 transition shadow-sm",
    danger: "bg-red-500 text-white text-sm font-semibold px-4 py-1.5 rounded hover:bg-red-600 transition shadow-sm",
    ghost: "bg-gray-200 text-gray-700 text-sm font-medium px-4 py-1.5 rounded hover:bg-gray-300 transition",
  };

  useEffect(() => {
    fetchContractors();
  }, []);

  useEffect(() => {
    const hasPickupData =
      form.pickup_street?.trim() ||
      form.pickup_city?.trim() ||
      form.pickup_zip_code?.trim();

    setShowPickup(!!hasPickupData);
  }, [form.pickup_street, form.pickup_city, form.pickup_zip_code]);

  const fetchContractors = async () => {
    try {
      const res = await api.get("/contractors");
      setContractors(res.data);
    } catch (err) {
      console.error("Błąd pobierania klientów:", err);
    }
  };

  const openCreateModal = () => {
    setForm(emptyForm);
    setErrors({});
    setBackendError("");
    setEditingId(null);
    setIsModalOpen(true);
    setShowPickup(false);
  };

  const openEditModal = (contractor) => {
    const { id, created_at, ...rest } = contractor;
    setForm(rest);
    setErrors({});
    setBackendError("");
    setEditingId(contractor.id);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setForm(emptyForm);
    setErrors({});
    setBackendError("");
    setEditingId(null);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!form.name || form.name.trim() === "") {
      newErrors.name = "Pole obowiązkowe";
    } else if (form.name.length > 100) {
      newErrors.name = "Maksymalna długość to 100 znaków";
    }

    if (form.remarks && form.remarks.length > 256) {
      newErrors.remarks = "Maksymalna długość to 256 znaków";
    }

    if (!form.city || form.city.trim() === "") {
      newErrors.city = "Pole obowiązkowe";
    } else if (form.city.length > 30) {
      newErrors.city = "Maksymalna długość to 30 znaków";
    }

    if (form.pickup_city && form.pickup_city.length > 30) {
      newErrors.pickup_city = "Maksymalna długość to 30 znaków";
    }

    if (!form.street || form.street.trim() === "") {
      newErrors.street = "Pole obowiązkowe";
    } else if (form.street.length > 50) {
      newErrors.street = "Maksymalna długość to 50 znaków";
    }

    if (form.pickup_street && form.pickup_street.length > 50) {
      newErrors.pickup_street = "Maksymalna długość to 50 znaków";
    }

    const zipRegex = /^\d{2}-\d{3}$/;

    if (!form.zip_code || !zipRegex.test(form.zip_code)) {
      newErrors.zip_code = "Niepoprawny format (XX-XXX)";
    }

    if (form.pickup_zip_code && !zipRegex.test(form.pickup_zip_code)) {
      newErrors.pickup_zip_code = "Niepoprawny format (XX-XXX)";
    }

    if (form.phone && form.phone.trim() !== "" && form.phone.length > 20) {
      newErrors.phone = "Maksymalna długość to 20 znaków";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBackendError("");

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      if (editingId !== null) {
        await api.put(`/contractors/${editingId}`, form);
      } else {
        await api.post("/contractors", form);
      }
      fetchContractors();
      closeModal();
    } catch (err) {
      console.error("Błąd podczas zapisywania danych klienta: ", err.response?.data || err.message);

      const backendData = err.response?.data;
      if (backendData && typeof backendData === "object") {
        const newFieldErrors = {};
        for (const key in backendData) {
          newFieldErrors[key] = backendData[key];
        }
        setErrors(newFieldErrors);
      } else {
        setBackendError(backendData?.detail || "Unexpected error occurred");
      }
    }
  };

  const labels = {
    name: "Nazwa",
    street: "Ulica",
    zip_code: "Kod pocztowy",
    city: "Miasto",
    pickup_street: "Ulica",
    pickup_zip_code: "Kod pocztowy",
    pickup_city: "Miasto",
    email: "Email",
    contact_person: "Osoba kontaktowa",
    phone: "Telefon",
    remarks: "Uwagi",
    is_active: "Aktywny"
  };

  return (
    <div className="p-3 space-y-6">
      <div className="flex items-center justify-between">
        <button
          onClick={openCreateModal}
          className={btn.primary}
        >
          + Dodaj nowego klienta
        </button>

        <label className="flex items-center gap-3 cursor-pointer select-none text-sm font-medium text-gray-800">
          <span>Pokaż nieaktywnych</span>

          <div
            onClick={() => setShowInactive(!showInactive)}
            className={`w-11 h-6 flex items-center rounded-full p-1 transition-colors ${showInactive ? "bg-green-500" : "bg-gray-300"
              }`}
          >
            <div
              className={`bg-white w-4 h-4 rounded-full shadow transform transition-transform duration-200 ${showInactive ? "translate-x-5" : "translate-x-0"
                }`}
            />
          </div>
        </label>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 text-gray-600">
              <tr className="text-left text-sm font-semibold">
                <th className="px-6 py-3 align-bottom">Nazwa</th>
                <th className="px-6 py-3 align-bottom">Miasto</th>
                <th className="px-6 py-3 align-bottom">Ulica</th>
                <th className="px-6 py-3 align-bottom">Kod</th>
                <th className="px-6 py-3 align-bottom">Email</th>
                <th className="px-6 py-3 align-bottom">Telefon</th>
              </tr>

              <tr className="bg-white border-t border-gray-200">
                <th className="py-2 align-top">
                  <div className="px-6">
                    <input
                      type="text"
                      placeholder="Nazwa..."
                      value={filters.name}
                      onChange={(e) =>
                        setFilters({ ...filters, name: e.target.value })
                      }
                      className="w-full h-8 border border-gray-200 rounded-md px-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </th>

                <th className="py-2 align-top">
                  <div className="px-6">
                    <input
                      type="text"
                      placeholder="Miasto..."
                      value={filters.city}
                      onChange={(e) =>
                        setFilters({ ...filters, city: e.target.value })
                      }
                      className="w-full h-8 border border-gray-200 rounded-md px-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </th>

                <th className="py-2 align-top">
                  <div className="px-6">
                    <input
                      type="text"
                      placeholder="Ulica..."
                      value={filters.street}
                      onChange={(e) =>
                        setFilters({ ...filters, street: e.target.value })
                      }
                      className="w-full h-8 border border-gray-200 rounded-md px-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </th>

                <th className="py-2 align-top">
                  <div className="px-6">
                    <input
                      type="text"
                      placeholder="Kod..."
                      value={filters.zip}
                      onChange={(e) =>
                        setFilters({ ...filters, zip: e.target.value })
                      }
                      className="w-full h-8 border border-gray-200 rounded-md px-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </th>

                <th className="py-2 align-top">
                  <div className="px-6">
                    <input
                      type="text"
                      placeholder="Email..."
                      value={filters.email}
                      onChange={(e) =>
                        setFilters({ ...filters, email: e.target.value })
                      }
                      className="w-full h-8 border border-gray-200 rounded-md px-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </th>

                <th className="py-2 align-top">
                  <div className="px-6">
                    <input
                      type="text"
                      placeholder="Telefon..."
                      value={filters.phone}
                      onChange={(e) =>
                        setFilters({ ...filters, phone: e.target.value })
                      }
                      className="w-full h-8 border border-gray-200 rounded-md px-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {contractors
                .filter((c) => showInactive || c.is_active)
                .filter(
                  (c) =>
                    c.name.toLowerCase().includes(filters.name.toLowerCase()) &&
                    c.city.toLowerCase().includes(filters.city.toLowerCase()) &&
                    c.street
                      .toLowerCase()
                      .includes(filters.street.toLowerCase()) &&
                    c.zip_code
                      .toLowerCase()
                      .includes(filters.zip.toLowerCase()) &&
                    c.email
                      .toLowerCase()
                      .includes(filters.email.toLowerCase()) &&
                    c.phone
                      .toLowerCase()
                      .includes(filters.phone.toLowerCase())
                )
                .map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => openEditModal(c)}
                    className={`cursor-pointer transition-colors hover:bg-gray-50 ${c.is_active ? "bg-white" : "bg-red-50 text-red-700"
                      }`}
                  >
                    <td className="px-6 py-3 font-medium text-gray-800">
                      {c.name}
                    </td>
                    <td className="px-6 py-3 text-gray-600">{c.city}</td>
                    <td className="px-6 py-3 text-gray-600">{c.street}</td>
                    <td className="px-6 py-3 text-gray-600">{c.zip_code}</td>
                    <td className="px-6 py-3 text-gray-600">{c.email}</td>
                    <td className="px-6 py-3 text-gray-600">{c.phone}</td>
                  </tr>
                ))}
            </tbody>
          </table>


          {isModalOpen && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">

              <div className="bg-white w-[520px] max-h-[90vh] rounded-xl shadow-2xl flex flex-col overflow-hidden">

                <div className="p-4 border-b bg-gray-50">
                  <h2 className="text-xl font-bold text-gray-800">
                    {editingId !== null ? "Edytuj klienta" : "Dodaj klienta"}
                  </h2>

                  {backendError && (
                    <div className="mt-2 text-red-600 font-medium">
                      {backendError}
                    </div>
                  )}
                </div>

                <form
                  onSubmit={handleSubmit}
                  className="flex-1 overflow-y-auto p-4 grid grid-cols-[160px_1fr] gap-x-4 gap-y-2 items-center"
                >

                  {formFields.map((key) => {
                    if (
                      key === "pickup_street" ||
                      key === "pickup_zip_code" ||
                      key === "pickup_city"
                    ) return null;

                    if (key === "is_active") {
                      return (
                        <div key={key} className="contents">

                          <div className="col-span-2 flex items-center justify-between border-t pb-1"></div>

                          <label className="text-sm font-medium text-gray-700">
                            {labels[key]}
                          </label>

                          <div className="flex items-center justify-end w-full">
                            <div
                              onClick={() =>
                                setForm({ ...form, is_active: !form.is_active })
                              }
                              className={`w-11 h-6 flex items-center rounded-full p-1 cursor-pointer transition-colors ${form.is_active ? "bg-green-500" : "bg-gray-300"
                                }`}
                            >
                              <div
                                className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-200 ${form.is_active ? "translate-x-5" : "translate-x-0"
                                  }`}
                              />
                            </div>
                          </div>

                        </div>
                      );
                    }

                    return (
                      <>
                        <label className="text-sm font-medium text-gray-700">
                          {labels[key]}
                        </label>

                        <input
                          className={`w-full border rounded-lg p-2 text-sm focus:outline-none focus:ring-2 ${errors[key]
                            ? "border-red-500 focus:ring-red-400"
                            : "focus:ring-blue-400"
                            }`}
                          value={form[key] || ""}
                          maxLength={
                            key === "name"
                              ? 100
                              : key === "city"
                                ? 30
                                : key === "street"
                                  ? 50
                                  : key === "phone"
                                    ? 20
                                    : key === "remarks"
                                      ? 256
                                      : undefined
                          }
                          onChange={(e) =>
                            setForm({ ...form, [key]: e.target.value })
                          }
                        />

                        <div className="col-span-2 -mt-3">
                          {errors[key] && (
                            <span className="text-red-500 text-sm">
                              {errors[key]}
                            </span>
                          )}
                        </div>
                      </>
                    );
                  })}

                  <div className="col-span-2 border-t pt-3 pb-3 mt-1 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">
                      Inny adres odbioru
                    </span>

                    <div
                      onClick={() => setShowPickup(!showPickup)}
                      className={`w-11 h-6 flex items-center rounded-full p-1 cursor-pointer transition-colors ${showPickup ? "bg-green-500" : "bg-gray-300"
                        }`}
                    >
                      <div
                        className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-200 ${showPickup ? "translate-x-5" : "translate-x-0"
                          }`}
                      />
                    </div>
                  </div>

                  {showPickup && (
                    <>
                      <label className="text-sm font-medium text-gray-700">
                        Miasto
                      </label>

                      <input
                        className={`w-full border rounded-lg p-2 text-sm ${errors.pickup_city
                          ? "border-red-500"
                          : ""
                          }`}
                        value={form.pickup_city || ""}
                        onChange={(e) =>
                          setForm({ ...form, pickup_city: e.target.value })
                        }
                      />

                      <div className="col-span-2 -mt-3">
                        {errors.pickup_city && (
                          <span className="text-red-500 text-sm">
                            {errors.pickup_city}
                          </span>
                        )}
                      </div>

                      <label className="text-sm font-medium text-gray-700">
                        Kod pocztowy
                      </label>

                      <input
                        className={`w-full border rounded-lg p-2 text-sm ${errors.pickup_zip_code
                          ? "border-red-500"
                          : ""
                          }`}
                        value={form.pickup_zip_code || ""}
                        onChange={(e) =>
                          setForm({ ...form, pickup_zip_code: e.target.value })
                        }
                      />

                      <div className="col-span-2 -mt-3">
                        {errors.pickup_zip_code && (
                          <span className="text-red-500 text-sm">
                            {errors.pickup_zip_code}
                          </span>
                        )}
                      </div>

                      <label className="text-sm font-medium text-gray-700">
                        Ulica
                      </label>

                      <input
                        className={`w-full border rounded-lg p-2 text-sm ${errors.pickup_street
                          ? "border-red-500"
                          : ""
                          }`}
                        value={form.pickup_street || ""}
                        onChange={(e) =>
                          setForm({ ...form, pickup_street: e.target.value })
                        }
                      />

                      <div className="col-span-2 -mt-3">
                        {errors.pickup_street && (
                          <span className="text-red-500 text-sm">
                            {errors.pickup_street}
                          </span>
                        )}
                      </div>
                    </>
                  )}

                  <div className="col-span-2 flex justify-end gap-3 pt-4 border-t mt-2">

                    <button
                      type="button"
                      onClick={closeModal}
                      className={btn.danger}
                    >
                      Anuluj
                    </button>

                    <button
                      type="submit"
                      className={btn.success}
                    >
                      Zapisz
                    </button>

                  </div>

                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}